/**
 * NicheLanding — single reusable component that renders an SEO-focused
 * landing page from a `nichePages.js` config object.
 *
 * Per-page concerns (kept compact, easy to scan):
 *  - <title>, <meta description>, OG tags applied imperatively on mount
 *  - JSON-LD `FAQPage` + `Product` schemas injected into <head>
 *  - Canonical link to the current path
 *  - Same visual language as Landing.jsx (sticky header, hero, features, FAQ, CTA)
 *  - Internal links to sibling niche pages (boosts crawlability + topical depth)
 */
/* eslint-disable react/no-unescaped-entities */
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight, CheckSquare, ChevronDown, FileText, Image as ImageIcon,
  MessageSquare, Send, Smartphone, Wrench, Zap, Boxes,
  ClipboardList, HardHat, Droplets, Plug, Wind, Hammer, BookOpen, Building2,
} from "lucide-react";
import Logo from "@/components/Logo";
import { findNiche, siblingNiches } from "@/data/nichePages";

const ICONS = {
  smartphone: Smartphone,
  image: ImageIcon,
  "file-text": FileText,
  zap: Zap,
  "check-square": CheckSquare,
  send: Send,
  boxes: Boxes,
  wrench: Wrench,
  "message-square": MessageSquare,
  "clipboard-list": ClipboardList,
  "hard-hat": HardHat,
  droplets: Droplets,
  plug: Plug,
  wind: Wind,
  hammer: Hammer,
  "book-open": BookOpen,
  building: Building2,
};

const BASE_URL = {
  fi: "https://veraso.fi",
  en: "https://verasoreports.com",
};

/* Track Plausible custom events without crashing if the script is missing. */
const track = (eventName) => {
  if (typeof window !== "undefined" && typeof window.plausible === "function") {
    try { window.plausible(eventName); } catch { /* no-op */ }
  }
};

/* -------------------------------------------------------------------------- */
/*  <head> updater — SEO meta + JSON-LD                                        */
/* -------------------------------------------------------------------------- */
function applyHead(page) {
  if (typeof document === "undefined") return () => {};
  const isFi = page.lang === "fi";
  const url = `${BASE_URL[page.lang]}/${page.slug}`;
  const altUrl = `${BASE_URL[isFi ? "en" : "fi"]}/`;

  document.documentElement.lang = page.lang;
  const prevTitle = document.title;
  document.title = page.title;

  const upsertMeta = (selector, attrName, attrValue) => {
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement("meta");
      const m = selector.match(/meta\[(name|property)="([^"]+)"\]/);
      if (m) el.setAttribute(m[1], m[2]);
      document.head.appendChild(el);
    }
    el.setAttribute(attrName, attrValue);
  };

  upsertMeta('meta[name="description"]', "content", page.description);
  upsertMeta('meta[name="keywords"]', "content", page.keyword);
  upsertMeta('meta[property="og:title"]', "content", page.title);
  upsertMeta('meta[property="og:description"]', "content", page.description);
  upsertMeta('meta[property="og:locale"]', "content", isFi ? "fi_FI" : "en_US");
  upsertMeta('meta[property="og:url"]', "content", url);
  upsertMeta('meta[property="og:type"]', "content", "website");
  const ogImg = `${BASE_URL[page.lang]}/brand/${isFi ? "og-veraso-fi.jpg" : "og-veraso-en.jpg"}`;
  upsertMeta('meta[property="og:image"]', "content", ogImg);
  upsertMeta('meta[name="twitter:card"]', "content", "summary_large_image");
  upsertMeta('meta[name="twitter:image"]', "content", ogImg);

  // Canonical
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);

  // hreflang alternates
  const ensureHrefLang = (hl, href) => {
    let link = document.querySelector(`link[rel="alternate"][hreflang="${hl}"]`);
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", hl);
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);
  };
  ensureHrefLang(page.lang, url);
  ensureHrefLang(isFi ? "en" : "fi", altUrl);
  ensureHrefLang("x-default", `${BASE_URL.fi}/`);

  // JSON-LD: FAQPage + Product
  const ldId = "veraso-niche-jsonld";
  let scriptEl = document.getElementById(ldId);
  if (!scriptEl) {
    scriptEl = document.createElement("script");
    scriptEl.type = "application/ld+json";
    scriptEl.id = ldId;
    document.head.appendChild(scriptEl);
  }
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const productLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Veraso Reports",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    offers: {
      "@type": "Offer",
      price: "39",
      priceCurrency: "EUR",
      url: `${BASE_URL[page.lang]}/register`,
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "12",
    },
    description: page.description,
  };
  scriptEl.text = JSON.stringify([faqLd, productLd]);

  return () => {
    document.title = prevTitle;
    const stale = document.getElementById(ldId);
    if (stale) stale.remove();
  };
}

/* -------------------------------------------------------------------------- */
export default function NicheLanding() {
  const location = useLocation();
  const slug = (location.pathname || "").replace(/^\/+/, "").replace(/\/+$/, "");
  // Lang resolution: route slug uniquely identifies a language, so just find it
  const page = findNiche(slug, "fi") || findNiche(slug, "en");

  useEffect(() => {
    if (!page) return;
    const cleanup = applyHead(page);
    return cleanup;
  }, [page]);

  // Hide Emergent badge on marketing pages
  useEffect(() => {
    // Capture signup attribution (utm_*, gclid, referrer) on niche landings too
    import("@/lib/analytics").then(({ captureSignupSource }) => captureSignupSource()).catch(() => {});

    const badge = document.getElementById("emergent-badge");
    const prev = badge ? badge.style.display : null;
    if (badge) badge.style.display = "none";
    return () => { if (badge) badge.style.display = prev || ""; };
  }, []);

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-slate-700">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold">Sivua ei löytynyt</h1>
          <Link to="/" className="mt-3 inline-block text-emerald-600 hover:underline">← Etusivulle</Link>
        </div>
      </div>
    );
  }

  const isFi = page.lang === "fi";
  const t = isFi ? FI_LABELS : EN_LABELS;
  const siblings = siblingNiches(page, 4);

  return (
    <div className="bg-white text-slate-900 font-sans antialiased">
      <Header isFi={isFi} t={t} />
      <Hero page={page} t={t} />
      <Features features={page.features} t={t} />
      <Proof page={page} t={t} />
      <Faq faqs={page.faqs} t={t} />
      <FinalCta page={page} t={t} />
      <Related siblings={siblings} t={t} />
      <Footer isFi={isFi} t={t} />
    </div>
  );
}

const FI_LABELS = {
  navHome: "Etusivu",
  navLogin: "Kirjaudu",
  navStart: "Aloita ilmaiseksi",
  faqTitle: "Usein kysytyt kysymykset",
  relatedTitle: "Tutustu myös",
  whyTitle: "Mitä saat",
  featuresTitle: "Näin se toimii",
  proofTitle: "Esimerkkinäkymä",
  proofSub: "Näin valmis raportti näyttää asiakkaalle.",
  finalCtaButton: "Aloita ilmaiseksi",
  footerCopy: "© Veraso Reports. Kenttäraportointi, joka näyttää ammattimaiselta.",
  homeLabel: "Tutustu Veraso Reportsiin",
};
const EN_LABELS = {
  navHome: "Home",
  navLogin: "Sign in",
  navStart: "Start free",
  faqTitle: "Frequently asked questions",
  relatedTitle: "Related pages",
  whyTitle: "What you get",
  featuresTitle: "How it works",
  proofTitle: "Sample report",
  proofSub: "This is what the customer sees when you share the report.",
  finalCtaButton: "Start free",
  footerCopy: "© Veraso Reports. Field reporting that looks professional.",
  homeLabel: "Learn more about Veraso Reports",
};

/* -------------------------------------------------------------------------- */
function Header({ isFi, t }) {
  return (
    <header className="sticky top-0 z-40 backdrop-blur bg-white/85 border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-1 font-display text-base font-bold tracking-tight" data-testid="niche-home-link">
          <span className="text-blue-900">Veraso</span>{" "}
          <span className="text-emerald-600">Reports</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login" className="hidden sm:inline-flex text-sm font-semibold text-slate-700 hover:text-slate-900" data-testid="niche-login-link">
            {t.navLogin}
          </Link>
          <Link
            to="/register"
            onClick={() => track("Niche CTA — Header")}
            className="inline-flex h-10 items-center justify-center px-5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
            data-testid="niche-start-cta"
          >
            {t.navStart}
          </Link>
        </div>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
function Hero({ page, t }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-emerald-50/50 via-white to-white" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-3" data-testid="niche-eyebrow">
              {page.eyebrow}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-slate-900" data-testid="niche-h1">
              {page.h1}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed max-w-xl" data-testid="niche-subhead">
              {page.subhead}
            </p>

            <ul className="mt-7 space-y-2.5">
              {page.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-slate-700">
                  <CheckSquare className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <Link
                to="/register"
                onClick={() => track(`Niche CTA — Hero — ${page.slug}`)}
                className="inline-flex h-12 items-center justify-center px-6 rounded-lg bg-emerald-600 text-white text-base font-semibold hover:bg-emerald-700"
                data-testid="niche-hero-cta"
              >
                {t.navStart} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900">
                {t.homeLabel} →
              </Link>
            </div>
          </div>

          <div className="lg:pl-6">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-xl shadow-emerald-900/5 overflow-hidden">
              <img
                src={page.image}
                alt={page.imageAlt}
                className="w-full h-auto block"
                loading="eager"
                data-testid="niche-hero-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
function Features({ features, t }) {
  return (
    <section className="py-16 sm:py-24 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 text-center">
          {t.featuresTitle}
        </h2>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = ICONS[f.icon] || CheckSquare;
            return (
              <div key={i} className="rounded-2xl border border-slate-200 p-6 bg-white hover:border-emerald-200 transition-colors">
                <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
function Proof({ page, t }) {
  return (
    <section className="py-16 sm:py-24 bg-slate-50 border-y border-slate-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{t.proofTitle}</h2>
        <p className="mt-3 text-slate-600">{t.proofSub}</p>
        <div className="mt-10 rounded-2xl bg-white border border-slate-200 shadow-xl shadow-emerald-900/5 overflow-hidden">
          <img src={page.image} alt={page.imageAlt} className="w-full h-auto block" loading="lazy" />
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
function Faq({ faqs, t }) {
  return (
    <section className="py-16 sm:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 text-center">
          {t.faqTitle}
        </h2>
        <div className="mt-10 space-y-3" data-testid="niche-faq-list">
          {faqs.map((f, i) => (
            <details
              key={i}
              className="group rounded-xl border border-slate-200 bg-white px-5 py-4 open:shadow-sm"
              data-testid={`niche-faq-${i}`}
            >
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <span className="font-display text-base font-semibold text-slate-900 pr-3">{f.q}</span>
                <ChevronDown className="h-4 w-4 text-slate-400 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
function FinalCta({ page, t }) {
  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-white to-emerald-50/60">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">
          {page.ctaTitle}
        </h2>
        <p className="mt-4 text-slate-700 text-lg">{page.ctaSub}</p>
        <Link
          to="/register"
          onClick={() => track(`Niche CTA — Final — ${page.slug}`)}
          className="mt-8 inline-flex h-12 items-center justify-center px-7 rounded-lg bg-emerald-600 text-white text-base font-bold hover:bg-emerald-700"
          data-testid="niche-final-cta"
        >
          {t.finalCtaButton} <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
function Related({ siblings, t }) {
  if (!siblings.length) return null;
  return (
    <section className="py-12 border-t border-slate-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-5">
          {t.relatedTitle}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="niche-related">
          {siblings.map((s) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              className="block rounded-xl border border-slate-200 p-4 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">{s.eyebrow}</div>
              <div className="mt-1 font-display font-bold text-slate-900 leading-tight">{s.keyword}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
function Footer({ isFi, t }) {
  return (
    <footer className="border-t border-slate-100 py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
        <div className="flex items-center gap-2">
          <Logo className="h-6" />
          <span>{t.footerCopy}</span>
        </div>
        <div className="flex items-center gap-5">
          <Link to="/" className="hover:text-slate-700">{t.navHome}</Link>
          <Link to="/login" className="hover:text-slate-700">{t.navLogin}</Link>
          <Link to="/register" className="hover:text-slate-700">{t.navStart}</Link>
        </div>
      </div>
    </footer>
  );
}
